import { KeyboardEvent, useRef, useState } from 'react';
import { Keyboard, MapPin, ArrowRight, Package } from 'lucide-react';
import { cn } from 'utils/classnames';
import { DISPOSITION_COLORS, SCREEN_TYPES } from 'utils/constants';
import { useAppStore } from 'store';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { useMoveStore } from 'store/moveStore';
import { scanLocation } from 'services/api/palletBuild';
import { playErrorSound, playGoodSound } from 'utils/sounds';

export const MoveDestinationPage = () => {
  const [scanValue, setScanValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(inputRef);
  const { setCurrentScreen } = useAppStore();
  const { pallet, setLocation } = useMoveStore();

  const handleSubmit = async () => {
    const trimmed = scanValue.trim();
    if (!trimmed) return;
    setIsLoading(true);
    setHasError(false);
    try {
      const data = await scanLocation(trimmed);
      if (data.success && data.data.length > 0) {
        setLocation(data.data[0]);
        playGoodSound();
        setScanValue('');
        setCurrentScreen(SCREEN_TYPES.MOVE_CONFIRM);
      } else {
        setScanValue('');
        setHasError(true);
        playErrorSound();
      }
    } catch {
      setScanValue('');
      setHasError(true);
      playErrorSound();
    } finally {
      setIsLoading(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const onBack = () => {
    setScanValue('');
    setHasError(false);
    setCurrentScreen(SCREEN_TYPES.MOVE_SCAN);
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 py-4',
        hasError && `-mx-4 -mb-6 min-h-full px-6 pb-6 pt-4 ${DISPOSITION_COLORS.PROBLEM.gradient}`
      )}
    >
      <div className="flex flex-col items-center gap-1">
        <span
          className={cn(
            'text-xs font-semibold uppercase tracking-widest',
            hasError ? 'text-white/80' : 'text-blue-500'
          )}
        >
          Move Pallet
        </span>
        <h2 className={cn('text-2xl font-bold', hasError ? 'text-white' : 'text-slate-800')}>
          {hasError ? "Location Doesn't Exist" : 'Scan Destination'}
        </h2>
        {hasError && (
          <p className="text-3xl font-bold text-white text-center">
            Please scan the destination location again.
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100">
          <Package className="h-4 w-4 text-blue-600" />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">
            Pallet
          </span>
          <span className="font-mono text-sm font-bold text-slate-700 truncate">
            {pallet?.location_id ?? '—'}
          </span>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 mx-1" />
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100">
          <MapPin className="h-4 w-4 text-slate-400" />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Destination
          </span>
          <span className="text-sm font-medium text-slate-400 italic">Not scanned</span>
        </div>
      </div>

      {pallet && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
            <Package className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Pallet Info
            </span>
          </div>
          <div className="grid grid-cols-3 divide-x divide-slate-100 px-0">
            <div className="flex flex-col items-center gap-0.5 px-4 py-3">
              <span className="text-xs text-slate-400 uppercase tracking-wider">Status</span>
              <span className="text-sm font-semibold text-slate-700 capitalize">
                {pallet.status}
              </span>
            </div>
            <div className="flex flex-col items-center gap-0.5 px-4 py-3">
              <span className="text-xs text-slate-400 uppercase tracking-wider">Cases</span>
              <span className="text-sm font-semibold text-slate-700">{pallet.carton_cnt}</span>
            </div>
            <div className="flex flex-col items-center gap-0.5 px-4 py-3">
              <span className="text-xs text-slate-400 uppercase tracking-wider">Products</span>
              <span className="text-sm font-semibold text-slate-700">{pallet.product_cnt}</span>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label className="mb-2 block text-sm font-semibold text-slate-600">Input Field</Label>
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            type="text"
            inputMode={keyboardVisible ? 'text' : 'none'}
            placeholder="Scan destination location..."
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => requestAnimationFrame(() => inputRef.current?.focus())}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            className="text-base"
            disabled={isLoading}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={toggleKeyboardInput}
            aria-pressed={keyboardVisible}
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="buttons-container !bg-transparent">
        <Button onClick={onBack} variant="outline" className="flex-1 h-12">
          Back
        </Button>
      </div>
    </div>
  );
};
